import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReportShell, { reportStyle, formatReportTimestamp } from '../../../shared/ReportShell';
import ReportOverlay from '../../../shared/ReportOverlay';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { formatNumber } from '../../../shared/numberFormat';

// Sales' own Integrity Report (issue #292) — first occupant of the
// 'sales-reports' nav stub, same shell as Purchasing's own
// (PurchasingIntegrityReportPage.js). One check so far, so no rule
// checklist or filters: every Sales Order line whose Shipped qty doesn't
// match the total of its shipments that haven't been reversed.
function SalesIntegrityReportPage() {
  const navigate = useNavigate();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  async function handleRun() {
    setLoading(true);
    try {
      const res = await axios.get('/api/sales-integrity-report/run');
      setResults(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Failed to run integrity report');
      setResults(null);
    } finally {
      setLoading(false);
    }
  }

  const qty = (value) => formatNumber(value, { maxDecimals: 4 });

  return (
    <div style={{ width: '100%', maxWidth: 900, margin: '32px auto 48px' }}>
      <div style={{ background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24, maxWidth: 480 }}>
        <h2 style={{ margin: '0 0 16px', fontWeight: 600, fontSize: 20 }}>Sales Integrity</h2>
        <div style={{ color: '#666', fontSize: 13, marginBottom: 16 }}>
          Every Sales Order line whose Shipped quantity doesn't match the total of its shipments, leaving out reversed shipments. Lines shipped before shipments recorded their items also show here.
        </div>

        <button type="button" onClick={handleRun} disabled={loading} style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}>
          {loading ? 'Running…' : 'Run'}
        </button>
        <button type="button" onClick={() => navigate('/sales-dashboard')} style={{ marginLeft: 8, padding: '8px 18px' }}>
          Cancel
        </button>
      </div>

      {results && (
        <ReportOverlay onClose={() => setResults(null)}>
          <ReportShell
            title="Sales Integrity Report"
            subtitle={<div>{formatReportTimestamp()}</div>}
            onClose={() => setResults(null)}
          >
            {results.length === 0 ? (
              <div style={{ color: '#888', fontSize: 14 }}>No issues found.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: reportStyle.tableFontSize }}>
                <thead>
                  <tr className="print-only-row"><td colSpan={6} style={{ height: 18, padding: 0, border: 'none' }} /></tr>
                  <tr style={{ textAlign: 'left', borderBottom: reportStyle.tableHeaderBorder }}>
                    <th style={{ padding: '6px 8px 6px 0', width: 120 }}>Order #</th>
                    <th style={{ padding: '6px 8px 6px 0' }}>Customer</th>
                    <th style={{ padding: '6px 8px 6px 0', width: 120 }}>SKU</th>
                    <th style={{ padding: '6px 8px 6px 0' }}>Name</th>
                    <th style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>Shipped</th>
                    <th style={{ padding: '6px 0 6px 0', textAlign: 'right' }}>Shipments total</th>
                  </tr>
                </thead>
                <tfoot>
                  <tr className="print-only-row"><td colSpan={6} style={{ height: 20, padding: 0, border: 'none' }} /></tr>
                </tfoot>
                <tbody>
                  {results.map((row) => (
                    <tr key={row.line_id} style={{ borderBottom: '1px solid #ddd', breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                      <td style={{ padding: '6px 8px 6px 0', fontWeight: 600 }}>{row.display_number}</td>
                      <td style={{ padding: '6px 8px 6px 0' }}>{row.customer_name}</td>
                      <td style={{ padding: '6px 8px 6px 0' }}>{row.sku}</td>
                      <td style={{ padding: '6px 8px 6px 0' }}>{row.name}</td>
                      <td style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>{qty(row.qty_shipped)}</td>
                      <td style={{ padding: '6px 0 6px 0', textAlign: 'right' }}>{qty(row.shipments_qty)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </ReportShell>
        </ReportOverlay>
      )}
    </div>
  );
}

export default SalesIntegrityReportPage;
